import React from "react";
import { NodeViewContent, NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { SelectField } from "components/input/SelectField";
import { SUPPORTED_LANGUAGES } from "./languageConfig";
import { CopyIcon } from "components/icons";

export const CodeBlockNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  extension,
  editor,
}) => {
  const currentLanguage = node.attrs.language || "plaintext";
  const isEditable = editor.isEditable;
  const [copied, setCopied] = React.useState(false);

  const languageOptions = SUPPORTED_LANGUAGES.map((lang) => ({
    label: lang.label,
    value: lang.value,
  }));

  const handleCopy = () => {
    const codeContent = node.textContent;
    navigator.clipboard.writeText(codeContent).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <NodeViewWrapper className="code-block-wrapper">
      <Box
        sx={{
          position: "relative",
          margin: "24px 0",
        }}
      >
        {/* Language Selector or Copy Button - Fixed at top-right */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            minWidth: isEditable ? 120 : "auto",
          }}
        >
          {isEditable ? (
            <SelectField
              name="language"
              value={currentLanguage}
              options={languageOptions}
              labelMappingKey="label"
              valueMappingKey="value"
              onValueChange={(value) => {
                updateAttributes({ language: value });
              }}
              size="small"
              hideHelperBox
              isSearchable
              placeholder="Language"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "rgba(30, 30, 30, 0.8)",
                  color: "#d4d4d4",
                  fontSize: "12px",
                  minHeight: "28px",
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "& .MuiSelect-select": {
                    padding: "4px 8px",
                    paddingRight: "24px !important",
                  },
                  "& .MuiSelect-icon": {
                    color: "#808080",
                  },
                },
              }}
            />
          ) : (
            <Tooltip title={copied ? "Copied!" : "Copy code"}>
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{
                  backgroundColor: "rgba(30, 30, 30, 0.8)",
                  color: copied ? "#4caf50" : "#d4d4d4",
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(40, 40, 40, 0.9)",
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  width: 28,
                  height: 28,
                }}
              >
                <CopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Code Block Content */}
        <pre>
          <NodeViewContent as="div" />
        </pre>
      </Box>
    </NodeViewWrapper>
  );
};
